import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, FileText, AlertTriangle, Wallet, Pencil, Eye, RefreshCw, Banknote, BookOpenCheck, FileDown, Briefcase, Info, Clock } from "lucide-react";
import VersamentiPendingCard from "@/components/VersamentiPendingCard";
import { useAuth } from "@/context/AuthContext";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { SospesiSummaryDialog } from "@/components/SospesiSummaryDialog";
import { CompagniaFilter } from "@/components/CompagniaFilter";
import { toast } from "sonner";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDate(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function formatDateLong(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const CHART_MARGIN = { top: 10, right: 20, bottom: 0, left: 0 };

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [live, setLive] = useState([]);
  const [byCompany, setByCompany] = useState(null);
  const [byCompanyRange, setByCompanyRange] = useState("sempre");
  const [loading, setLoading] = useState(true);
  const [sospDetail, setSospDetail] = useState({ open: false, loading: false, data: null });
  const [compFilter, setCompFilter] = useState("");
  const compParams = compFilter ? { params: { compagnia_id: compFilter } } : undefined;

  const openSospesiDetail = async () => {
    setSospDetail({ open: true, loading: true, data: null });
    try {
      const { data } = await api.get("/dashboard/sospesi-summary", compParams);
      setSospDetail({ open: true, loading: false, data });
    } catch (err) {
      toast.error(formatApiError(err));
      setSospDetail({ open: false, loading: false, data: null });
    }
  };

  const hasPlurimandatari = !!user?.agency?.plurimandatari_abilitato;

  const load = async () => {
    try {
      const [s, l, bc] = await Promise.all([
        api.get("/dashboard/stats", compParams),
        api.get("/dashboard/live-sheets"),
        hasPlurimandatari
          ? api.get(`/dashboard/by-company?range=${byCompanyRange}`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setStats(s.data);
      setLive(l.data);
      setByCompany(bc.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [byCompanyRange, hasPlurimandatari, compFilter]);

  if (loading) return <div className="text-slate-500 text-sm">Caricamento dashboard…</div>;
  if (!stats) return null;
  const isSoloPlan = user?.agency?.plan_id === "solo";

  const downloadDistintaPdf = async (id, dataD) => {
    try {
      const res = await api.get(`/conto-separato/distinte/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `distinta_${dataD}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const chartData = (stats.ultimi_7_giorni || []).map((d) => ({
    label: formatDate(d.date),
    Totale: d.total,
  }));

  const cards = [
    ...(isSoloPlan ? [] : [{ label: "Collaboratori attivi", value: stats.collaboratori_attivi, icon: Users, color: "text-blue-600", bg: "bg-blue-50", testId: "stat-collaboratori" }]),
    { label: "Fogli cassa aperti", value: stats.fogli_aperti, icon: FileText, color: "text-emerald-600", bg: "bg-emerald-50", testId: "stat-aperti" },
    { label: "Incassi di oggi", value: formatEUR(stats.incassi_oggi), icon: Wallet, color: "text-slate-900", bg: "bg-slate-100", testId: "stat-incassi-oggi" },
    { label: "Sospesi", value: formatEUR(stats.totale_sospesi || 0), icon: Clock, color: "text-amber-700", bg: "bg-amber-50", testId: "stat-sospesi", clickable: true, onClick: () => openSospesiDetail(), sublabel: stats.n_sospesi > 0 ? `${stats.n_sospesi} polizz${stats.n_sospesi === 1 ? "a sospesa" : "e sospese"}` : null, alert: stats.n_sospesi_oltre_30gg > 0 ? `${stats.n_sospesi_oltre_30gg} oltre 30 giorni · ${formatEUR(stats.totale_sospesi_oltre_30gg)}` : null },
    { label: "Urgenze oggi", value: stats.urgenti_oggi, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", testId: "stat-urgenti" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Panoramica</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">{isSoloPlan ? "Andamento dei tuoi incassi in tempo reale." : "Andamento incassi e attività dei collaboratori in tempo reale."}</p>
        </div>
        <CompagniaFilter value={compFilter} onChange={setCompFilter} testId="dashboard-compagnia" className="min-w-[200px]" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          c.clickable ? (
            <button
              key={c.label}
              type="button"
              onClick={c.onClick}
              className="text-left"
              data-testid={`${c.testId}-btn`}
            >
              <Card className="p-5 border-slate-200 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer" data-testid={c.testId}>
                <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center mb-3`}>
                  <c.icon className={`w-5 h-5 ${c.color}`} />
                </div>
                <div className="text-2xl font-heading font-bold text-slate-900 font-mono-num">{c.value}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  {c.label} <span className="text-[10px] text-amber-700">→ vedi dettaglio</span>
                </div>
                {c.sublabel && <div className="text-[11px] text-amber-700 mt-0.5">{c.sublabel}</div>}
                {c.alert && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={`${c.testId}-alert`}>{c.alert}</div>}
              </Card>
            </button>
          ) : (
            <Card key={c.label} className="p-5 border-slate-200" data-testid={c.testId}>
              <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center mb-3`}>
                <c.icon className={`w-5 h-5 ${c.color}`} />
              </div>
              <div className="text-2xl font-heading font-bold text-slate-900 font-mono-num">{c.value}</div>
              <div className="text-xs text-slate-500 mt-1">{c.label}</div>
              {c.sublabel && <div className="text-[11px] text-amber-700 mt-0.5">{c.sublabel}</div>}
              {c.alert && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={`${c.testId}-alert`}>{c.alert}</div>}
            </Card>
          )
        ))}
      </div>

      <VersamentiPendingCard />

      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ultimi 7 giorni</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Totale incassi giornalieri</h2>
          </div>
        </div>
        <div className="h-72" style={{ minHeight: 280 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={280}>
            <AreaChart data={chartData} margin={CHART_MARGIN}>
              <defs>
                <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0f172a" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0f172a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `€${v}`} />
              <Tooltip formatter={(v) => formatEUR(v)} labelClassName="text-slate-900" />
              <Area type="monotone" dataKey="Totale" stroke="#0f172a" strokeWidth={2} fill="url(#gradTotal)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Live sheets section */}
      {!isSoloPlan && (
      <Card className="p-6 border-slate-200" data-testid="live-sheets-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-emerald-600 font-semibold inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> In tempo reale
            </div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Fogli cassa aperti</h2>
            <p className="text-sm text-slate-500 mt-1">Aggiornamento automatico ogni 60 secondi. Clicca su un foglio per visualizzarlo o modificarlo.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-live"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Aggiorna</Button>
        </div>
        {live.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500" data-testid="live-empty">
            Nessun foglio cassa aperto in questo momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {live.map((row) => {
              const isPlaceholder = row.placeholder || !row.sheet?.id;
              const cardBase = "border rounded-lg p-4 transition-all";
              const cardStyle = isPlaceholder
                ? "border-dashed border-slate-300 bg-slate-50 hover:border-slate-400"
                : "border-slate-200 hover:border-slate-400 hover:shadow-sm";
              const displayName = row.is_agente
                ? "Agenzia"
                : `${row.user?.nome || ""} ${row.user?.cognome || ""}`.trim() || "—";
              const contoSep = !!row.user?.conto_separato;
              const autonoma = !!row.user?.chiusura_autonoma;
              const cardKey = row.sheet?.id || `placeholder-${row.user?.id || Math.random()}`;
              const cardTestId = row.sheet?.id ? `live-card-${row.sheet.id}` : `live-placeholder-${row.user?.id}`;
              const inner = (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className={`font-heading font-semibold truncate ${isPlaceholder ? "text-slate-500" : "text-slate-900"}`}>
                        {displayName}
                      </div>
                      <div className="text-xs text-slate-500">Foglio del {formatDateLong(row.sheet.data_foglio_cassa)}</div>
                    </div>
                    {isPlaceholder ? (
                      <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">
                        Non ancora aperto
                      </Badge>
                    ) : (
                      <Badge variant="outline" className={row.sheet.stato === "riaperto" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}>
                        {row.sheet.stato === "riaperto" ? "Riaperto" : "Aperto"}
                      </Badge>
                    )}
                  </div>
                  {(contoSep || autonoma) && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {contoSep && (
                        <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded font-semibold">
                          Conto separato
                        </span>
                      )}
                      {autonoma && (
                        <span className="text-[10px] uppercase tracking-wider bg-purple-100 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded font-semibold">
                          Chiusura autonoma
                        </span>
                      )}
                    </div>
                  )}
                  {isPlaceholder ? (
                    <div className="mt-4 text-xs text-slate-500 italic">
                      Il collaboratore non ha ancora inserito incassi oggi.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                        <div>
                          <div className="text-slate-500">Righe</div>
                          <div className="font-heading font-semibold text-slate-900">{row.n_entries}</div>
                        </div>
                        <div>
                          <div className="text-slate-500">Urgenti</div>
                          <div className={`font-heading font-semibold ${row.totals.count_urgenti > 0 ? "text-red-600" : "text-slate-900"}`}>
                            {row.totals.count_urgenti}
                          </div>
                        </div>
                        <div>
                          <div className="text-slate-500">Totale</div>
                          <div className="font-heading font-semibold text-slate-900 font-mono-num">{formatEUR(row.totals.totale_generale)}</div>
                        </div>
                      </div>
                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                        <span className="text-slate-500 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Modifica</span>
                        <Eye className="w-3.5 h-3.5 text-slate-400" />
                      </div>
                    </>
                  )}
                </>
              );
              if (isPlaceholder) {
                return (
                  <div key={cardKey} className={`${cardBase} ${cardStyle}`} data-testid={cardTestId}>
                    {inner}
                  </div>
                );
              }
              return (
                <Link
                  key={cardKey}
                  to={`/admin/foglio-cassa/${row.sheet.id}`}
                  className={`${cardBase} ${cardStyle}`}
                  data-testid={cardTestId}
                >
                  {inner}
                </Link>
              );
            })}
          </div>
        )}
      </Card>
      )}

      {/* Situazione per compagnia — visibile se l'agenzia ha plurimandatari attivo */}
      {hasPlurimandatari && byCompany && (
        <Card className="p-6 border-slate-200" data-testid="by-company-section">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Multi-compagnia
              </div>
              <h2 className="font-heading text-lg font-semibold text-slate-900">Situazione per compagnia</h2>
              <p className="text-sm text-slate-500 mt-1">
                Riepilogo economico per ciascuna compagnia con cui lavori. I saldi
                &ldquo;da versare&rdquo; e &ldquo;sospesi&rdquo; sono <strong>aperti a oggi</strong>, l&apos;incassato
                dipende dal periodo selezionato.
              </p>
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 self-start" data-testid="by-company-range">
              {[
                { key: "oggi", label: "Oggi" },
                { key: "7d", label: "7 giorni" },
                { key: "sempre", label: "Sempre" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setByCompanyRange(opt.key)}
                  className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap ${
                    byCompanyRange === opt.key
                      ? "bg-slate-900 text-white font-semibold"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid={`by-company-range-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {byCompany.rows.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500">
              Nessuna compagnia con movimenti aperti.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Compagnia</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Incassato periodo</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Da versare</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Sospesi</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">In attesa</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {byCompany.rows.map((r) => (
                    <tr
                      key={r.compagnia_id || "_no_comp_"}
                      className="hover:bg-slate-50"
                      data-testid={`by-company-row-${r.compagnia_id || "no-comp"}`}
                    >
                      <td className="px-3 py-3">
                        <div className="font-medium text-slate-900">{r.compagnia_nome}</div>
                        {!r.compagnia_attiva && (
                          <div className="text-[11px] text-slate-400 italic">disattivata</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono-num">
                        <div className="text-slate-900">{formatEUR(r.incassato_periodo)}</div>
                        <div className="text-[11px] text-slate-500">
                          {r.titoli_periodo} {r.titoli_periodo === 1 ? "polizza" : "polizze"}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono-num">
                        <div className={r.da_versare > 0 ? "text-emerald-700 font-semibold" : "text-slate-400"}>
                          {formatEUR(r.da_versare)}
                        </div>
                        {r.titoli_da_versare > 0 && (
                          <div className="text-[11px] text-slate-500">{r.titoli_da_versare} titoli</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono-num">
                        <div className={r.sospesi > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}>
                          {formatEUR(r.sospesi)}
                        </div>
                        {r.titoli_sospesi > 0 && (
                          <div className="text-[11px] text-slate-500">{r.titoli_sospesi} titoli</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono-num text-slate-700">
                        {r.in_attesa_convalida > 0 ? formatEUR(r.in_attesa_convalida) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50/60 font-semibold">
                  <tr>
                    <td className="px-3 py-3 text-slate-700">Totale</td>
                    <td className="px-3 py-3 text-right font-mono-num text-slate-900">{formatEUR(byCompany.totals.incassato_periodo)}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-emerald-700">{formatEUR(byCompany.totals.da_versare)}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-amber-700">{formatEUR(byCompany.totals.sospesi)}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-slate-700">{formatEUR(byCompany.totals.in_attesa_convalida)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      )}

      <SospesiSummaryDialog state={sospDetail} onOpenChange={(o) => setSospDetail((s) => ({ ...s, open: o }))} />
    </div>
  );
}
